/** Monthly from pricingOfferDetails array (Enfin and similar). */
export function pricingOfferDetailsMonthlyFromOffer(offer: any): number {
    if (!offer || offer.__error) return 0;
    const details =
        offer?.pricingOfferDetails ??
        offer?.PricingOfferDetails ??
        offer?.payload?.pricingOfferDetails ??
        offer?.payload?.PricingOfferDetails;
    if (!Array.isArray(details) || !details[0]) return 0;
    const monthly =
        details[0]?.monthlyPayment ??
        details[0]?.MonthlyPayment ??
        details[0]?.totalMonthlyPayment ??
        details[0]?.TotalMonthlyPayment;
    return typeof monthly === 'number' && Number.isFinite(monthly) ? monthly : 0;
}

/**
 * Extract monthly from a single pricing API offer (GoodLeap, IGS, LightReach, Enfin).
 * `partnerNorm` should be lowercase normalized partner label.
 */
export function monthlyFromOffer(offer: any, partnerNorm: string): number {
    if (offer?.__error) return 0;
    const payload = offer?.payload;

    if (Array.isArray(payload) && payload[0]) {
        const calc = payload[0]?.calculations?.[0] ?? payload[0]?.Calculations?.[0];
        const v =
            calc?.roundedAmount?.value ??
            calc?.RoundedAmount?.Value ??
            calc?.amount?.value ??
            calc?.Amount?.Value;
        if (typeof v === 'number') return v;
    }

    if (payload && !Array.isArray(payload)) {
        const firstPricing = payload.pricing?.[0] ?? payload.Pricing?.[0];
        const firstYear =
            firstPricing?.firstYearMonthlyPayment?.value ??
            firstPricing?.FirstYearMonthlyPayment?.Value;
        if (typeof firstYear === 'number') return firstYear;

        const firstMp = payload.monthlyPayments?.[0] ?? payload.MonthlyPayments?.[0];
        const mpVal =
            firstMp?.totalMonthlyPayment ??
            firstMp?.TotalMonthlyPayment ??
            firstMp?.monthlyPayment ??
            firstMp?.MonthlyPayment;
        if (typeof mpVal === 'number') return mpVal;

        const directVal =
            payload?.totalMonthlyPayment ??
            payload?.TotalMonthlyPayment ??
            payload?.monthlyPayment ??
            payload?.MonthlyPayment;
        if (typeof directVal === 'number') return directVal;
    }

    if (partnerNorm.includes('lightreach')) {
        const firstMp = offer?.monthlyPayments?.[0] ?? offer?.MonthlyPayments?.[0];
        const mpVal =
            firstMp?.totalMonthlyPayment ??
            firstMp?.TotalMonthlyPayment ??
            firstMp?.monthlyPayment ??
            firstMp?.MonthlyPayment;
        if (typeof mpVal === 'number') return mpVal;
    }

    if (partnerNorm.includes('enfin')) {
        const monthly = pricingOfferDetailsMonthlyFromOffer(offer);
        if (monthly > 0) return monthly;
    }

    const est = offer?.estimatedMonthlyPayment ?? offer?.EstimatedMonthlyPayment;
    return typeof est === 'number' ? est : 0;
}

/** Term (months/years as returned by API) and APR percent from ExcelOffer or top-level fields. */
export function termAprFromOffer(offer: any): { term: number | null; apr: number | null } {
    const excel = offer?.ExcelOffer ?? offer?.excelOffer ?? {};
    const termRaw = excel?.term ?? excel?.Term ?? offer?.term ?? offer?.Term;
    let aprRaw = excel?.apr ?? excel?.Apr ?? offer?.apr ?? offer?.APR;
    const term = Number(termRaw);
    let apr = Number(aprRaw);
    if (Number.isFinite(apr) && apr > 0 && apr < 1) {
        apr = +(apr * 100).toFixed(2);
    }
    return {
        term: Number.isFinite(term) && term > 0 ? term : null,
        apr: Number.isFinite(apr) ? apr : null,
    };
}

export function isFailedPricingOffer(offer: any): boolean {
    if (!offer) return true;
    if (offer.__error) return true;
    if (offer.IsSuccessful === false || offer.isSuccessful === false) return true;
    return false;
}

export function isRecommendedOffer(offer: any): boolean {
    if (!offer || isFailedPricingOffer(offer)) return false;
    const r = offer.Recommended ?? offer.recommended;
    if (r === true || r === 1) return true;
    if (typeof r === 'string') {
        const s = r.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'yes';
    }
    return false;
}

/** Financing grid cells expect numeric term/APR (0 when unknown). */
export function termAprNumbersFromOffer(offer: any): { term: number; apr: number } {
    const { term, apr } = termAprFromOffer(offer);
    return { term: term ?? 0, apr: apr ?? 0 };
}

/**
 * TPO starting rate in $/kWh (the "Minimum Rate" column in All pricing options). Accepts a pricing
 * offer or a grid `PriceCell`, both of which carry `ExcelOffer`. Only PPA / lease offers have a
 * workbook rate, so loan and cash return null.
 */
export function solarRateFromOffer(offer: any): number | null {
    const raw = offer?.ExcelOffer?.startingRate ?? offer?.ExcelOffer?.StartingRate;
    const n = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** `$0.140/kWh` — matches the 3-decimal rate formatting used across pricing surfaces. */
export function formatSolarRatePerKwh(rate: number): string {
    return `$${rate.toFixed(3)}/kWh`;
}

/** Recommended offer when flagged, else first successful offer in the row. */
export function chooseRecommendedPricingOffer(offers: any[]): any | null {
    const list = Array.isArray(offers) ? offers : [];
    const recommended = list.find((o) => isRecommendedOffer(o));
    if (recommended) return recommended;
    return list.find((o) => o && !isFailedPricingOffer(o)) ?? null;
}
